export interface IXmlValidationResult {
  isValid: boolean;
  error?: string;
}

const FALLBACK_XML_ERROR = 'XML не соответствует базовым синтаксическим правилам';

const isXmlNameToken = (value: string): boolean =>
  Boolean(value) && value.toLowerCase() !== 'unparsable' && /^[A-Za-z_][\w.-]*$/.test(value);

const formatTagMismatchMessage = (detail: string): string => {
  const tagsMatch = detail.trim().match(/^(\S+)(?:\s+line\s+\d+\s+and\s+(\S+))?/i);

  if (!tagsMatch) {
    return 'несовпадение открывающего и закрывающего тегов';
  }

  const openTag = tagsMatch[1];
  const closeTag = tagsMatch[2];

  if (isXmlNameToken(openTag) && isXmlNameToken(closeTag || '')) {
    return `несовпадение открывающего и закрывающего тегов: <${openTag}> и </${closeTag}>`;
  }

  if (isXmlNameToken(openTag)) {
    return `несовпадение открывающего и закрывающего тегов: <${openTag}>`;
  }

  return 'несовпадение открывающего и закрывающего тегов';
};

const PARSER_ERROR_TRANSLATIONS: Array<[RegExp, (match: RegExpMatchArray) => string]> = [
  [/StartTag:\s*invalid element name/i, () => 'некорректное имя элемента'],
  [/Opening and ending tag mismatch:\s*(.*)/i, match => formatTagMismatchMessage(match[1] || '')],
  [
    /Premature end of data in tag\s*(\S*)/i,
    match => (isXmlNameToken(match[1] || '') ? `незавершённый тег <${match[1]}>` : 'незавершённый тег'),
  ],
  [/Extra content at the end of the document/i, () => 'лишнее содержимое в конце документа'],
  [/xmlParseEntityRef:\s*no name/i, () => 'некорректная ссылка на сущность (&)'],
  [/Entity\s+'([^']+)'\s+not defined/i, match => `сущность «${match[1]}» не определена`],
  [/AttValue:\s*" or ' expected/i, () => 'ожидается значение атрибута в кавычках'],
  [/attributes construct error/i, () => 'ошибка в конструкции атрибутов'],
  [
    /Couldn't find end of Start Tag\s*(\S*)/i,
    match =>
      isXmlNameToken(match[1] || '')
        ? `не найден конец открывающего тега <${match[1]}>`
        : 'не найден конец открывающего тега',
  ],
  [/expected '>'/i, () => "ожидается символ '>'"],
  [
    /XML declaration allowed only at the start of the document/i,
    () => 'объявление XML допускается только в начале документа',
  ],
  [/Document is empty/i, () => 'документ пуст'],
  [/Space required after the Public Identifier/i, () => 'требуется пробел после публичного идентификатора'],
  [
    /Specification mandates value for attribute\s*(\S*)/i,
    match =>
      isXmlNameToken(match[1] || '')
        ? `для атрибута «${match[1]}» требуется значение`
        : 'для атрибута требуется значение',
  ],
  [/Unescaped '&' not allowed/i, () => "недопустимый неэкранированный символ '&'"],
  [/invalid character/i, () => 'недопустимый символ'],
];

const translateParserReason = (reason: string): string | undefined => {
  const normalized = reason.trim();

  return PARSER_ERROR_TRANSLATIONS.map(([pattern, toMessage]) => {
    const match = normalized.match(pattern);

    return match ? toMessage(match) : undefined;
  }).find(message => Boolean(message));
};

const localizeParserError = (rawText: string): string => {
  const text = rawText.replace(/\s+/g, ' ').trim();
  const match = text.match(
    /(?:error\s+)?on\s+line\s+(\d+)\s+at\s+column\s+(\d+)\s*:\s*(.+?)(?:\s+Below is a rendering.*)?$/i,
  );

  if (match) {
    const [, line, column, reason] = match;
    const localizedReason = translateParserReason(reason) || 'синтаксическая ошибка';

    return `Ошибка XML (строка ${line}, столбец ${column}): ${localizedReason}`;
  }

  const localized = translateParserReason(text);

  if (localized) {
    return `Ошибка XML: ${localized}`;
  }

  return FALLBACK_XML_ERROR;
};

const getParserErrorMessage = (doc: Document): string | undefined => {
  const parserError = doc.getElementsByTagName('parsererror')[0];

  if (!parserError) {
    return undefined;
  }

  const text = parserError.textContent?.trim();

  if (!text) {
    return FALLBACK_XML_ERROR;
  }

  return localizeParserError(text);
};

export const validateXml = (xml: string | undefined): IXmlValidationResult => {
  if (!xml?.trim()) {
    return {
      isValid: false,
      error: 'XML не может быть пустым',
    };
  }

  const doc = new DOMParser().parseFromString(xml, 'application/xml');
  const error = getParserErrorMessage(doc);

  if (error) {
    return {
      isValid: false,
      error,
    };
  }

  return { isValid: true };
};

const serializeNode = (node: Node, depth: number, indent: string): string => {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent?.trim();

    return text || '';
  }

  if (node.nodeType === Node.COMMENT_NODE) {
    return `${indent.repeat(depth)}<!--${node.textContent || ''}-->`;
  }

  if (node.nodeType === Node.CDATA_SECTION_NODE) {
    return `${indent.repeat(depth)}<![CDATA[${node.textContent || ''}]]>`;
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return '';
  }

  const element = node as Element;
  const prefix = indent.repeat(depth);
  const attrs = Array.from(element.attributes)
    .map(attr => ` ${attr.name}="${attr.value}"`)
    .join('');

  const children = Array.from(element.childNodes);
  const meaningfulChildren = children.filter(child => {
    if (child.nodeType === Node.TEXT_NODE) {
      return !!child.textContent?.trim();
    }

    return (
      child.nodeType === Node.ELEMENT_NODE ||
      child.nodeType === Node.COMMENT_NODE ||
      child.nodeType === Node.CDATA_SECTION_NODE
    );
  });

  if (meaningfulChildren.length === 0) {
    return `${prefix}<${element.tagName}${attrs}/>`;
  }

  const onlyText =
    meaningfulChildren.length === 1 && meaningfulChildren[0].nodeType === Node.TEXT_NODE
      ? meaningfulChildren[0].textContent?.trim() || ''
      : undefined;

  if (onlyText !== undefined) {
    return `${prefix}<${element.tagName}${attrs}>${onlyText}</${element.tagName}>`;
  }

  const childrenXml = meaningfulChildren
    .map(child => serializeNode(child, depth + 1, indent))
    .filter(Boolean)
    .join('\n');

  return `${prefix}<${element.tagName}${attrs}>\n${childrenXml}\n${prefix}</${element.tagName}>`;
};

export const formatXml = (xml: string | undefined): { formatted?: string; error?: string } => {
  const validation = validateXml(xml);

  if (!validation.isValid) {
    return { error: validation.error };
  }

  const doc = new DOMParser().parseFromString(xml!.trim(), 'application/xml');
  const declarationMatch = xml!.trim().match(/^<\?xml[^?]*\?>/);
  const root = doc.documentElement;

  if (!root) {
    return { error: 'XML не соответствует базовым синтаксическим правилам' };
  }

  const formattedBody = serializeNode(root, 0, '  ');
  const formatted = declarationMatch ? `${declarationMatch[0]}\n${formattedBody}` : formattedBody;

  return { formatted };
};
